import React, { useRef, useState } from "react";
import { Box, Typography, LinearProgress, IconButton } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import {
    uploadServiceDocument,
    deleteServiceDocument,
} from "../../Api/ServiceRecordApi";
import { useToast } from "../../Hooks/useToast";

// Human-readable file size, e.g. 1258291 -> "1.2MB".
const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return "";
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
};

// Prefer the backend's user-facing upload error (e.g. "File exceeds the maximum
// size of 5 MB"); fall back to a generic message for missing/technical errors.
const friendlyUploadError = (err) => {
    const msg =
        err?.response?.data?.responseStatus?.msg ||
        err?.response?.data?.message ||
        err?.responseStatus?.msg;
    if (!msg || typeof msg !== "string") return "";
    const technical = /(traceback|exception|<class|sqlalchemy|errno|at 0x)/i.test(msg);
    if (technical || msg.length > 140) return "";
    return msg;
};

const hasAcceptedExtension = (fileName, acceptedExtensions = []) => {
    if (!acceptedExtensions.length) return true;
    const lower = (fileName || "").toLowerCase();
    return acceptedExtensions.some((ext) => lower.endsWith(ext.toLowerCase()));
};

// Icon glyph choice (Figma "Icons used"): document vs image.
const IMAGE_EXTS = [".png", ".jpg", ".jpeg", ".gif", ".webp", ".svg", ".bmp"];
const isImageName = (name = "") =>
    IMAGE_EXTS.some((e) => (name || "").toLowerCase().endsWith(e));
// Empty slot has no file yet: pick by docType when it accepts only images.
const isImageDocType = (docType) => {
    const exts = docType?.acceptedExtensions || [];
    return exts.length > 0 && exts.every((e) => IMAGE_EXTS.includes(e.toLowerCase()));
};

// A rounded file-type tile. Default is a white tile with a subtle shadow and a
// grey glyph; the empty slot's hover swaps it to a flat lavender tile with a
// purple glyph via the `.doc-icon` parent-hover selector.
const IconTile = ({ color = "#667085", bg = "#FFFFFF", IconComp = DescriptionOutlinedIcon, shadow = true }) => (
    <Box
        className="doc-icon"
        sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            backgroundColor: bg,
            boxShadow: shadow ? "0px 1px 2px rgba(16, 24, 40, 0.06)" : "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
        }}
    >
        <IconComp sx={{ color, fontSize: 22 }} />
    </Box>
);

const ROW = {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    borderRadius: "16px",
    padding: "14px 18px",
    // Let the row shrink so the (ellipsis) file name doesn't force overflow.
    minWidth: 0,
};

// Reusable staged-document upload slot. docType comes from orgMeta
// (components.replacement.documentTypes). Lifts the staged documentID up via
// onChange(docTypeCode, { documentID, fileName, sizeBytes } | null).
export default function DocumentUpload({
    docType,
    batteryID,
    maxFileSizeMb = 5,
    value = null,
    error = false,
    onChange = () => {},
}) {
    const { openNotification } = useToast();
    const inputRef = useRef(null);
    const lastFileRef = useRef(null);

    const [status, setStatus] = useState(value?.documentID ? "uploaded" : "empty");
    const [fileName, setFileName] = useState(value?.fileName || "");
    const [sizeBytes, setSizeBytes] = useState(value?.sizeBytes ?? null);
    const [progress, setProgress] = useState(0);
    const [errorMsg, setErrorMsg] = useState("");

    const acceptAttr = (docType?.acceptedExtensions || []).join(",");
    // Accepted extensions + the max size, shown under the empty slot.
    const extLabel = [
        ...(docType?.acceptedExtensions || []),
        `max ${maxFileSizeMb} MB`,
    ].join(" · ");
    const requiredMark = docType?.required ? (
        <span style={{ color: "#E5484D", marginLeft: 4 }}>*</span>
    ) : null;

    // Empty slot: glyph from docType; once a file exists: glyph from its name.
    const EmptyIcon = isImageDocType(docType) ? ImageOutlinedIcon : DescriptionOutlinedIcon;
    const FileIcon = isImageName(fileName) ? ImageOutlinedIcon : DescriptionOutlinedIcon;

    const openPicker = () => inputRef.current?.click();

    const doUpload = async (file) => {
        lastFileRef.current = file;
        setFileName(file.name);
        setSizeBytes(file.size);
        setProgress(0);
        setErrorMsg("");
        setStatus("uploading");
        try {
            const res = await uploadServiceDocument(
                file,
                docType.code,
                batteryID,
                (e) => {
                    if (e?.total) setProgress(Math.round((e.loaded / e.total) * 100));
                }
            );
            const body = res?.response?.data ?? res?.response ?? {};
            const documentID = body?.documentID;
            if (!documentID) {
                setErrorMsg(friendlyUploadError({ responseStatus: res?.responseStatus }));
                setStatus("failed");
                return;
            }
            setStatus("uploaded");
            onChange(docType.code, {
                documentID,
                fileName: body?.fileName || file.name,
                sizeBytes: body?.sizeBytes ?? file.size,
            });
        } catch (err) {
            console.error("Document upload failed:", err);
            setErrorMsg(friendlyUploadError(err));
            setStatus("failed");
        }
    };

    const handleFileSelected = (event) => {
        const file = event.target.files?.[0];
        event.target.value = ""; // allow re-selecting the same file
        if (!file) return;

        if (!hasAcceptedExtension(file.name, docType?.acceptedExtensions)) {
            openNotification({ text: `Unsupported file type. Allowed: ${extLabel}`, icon: "toast-error" });
            return;
        }
        if (file.size > maxFileSizeMb * 1024 * 1024) {
            openNotification({ text: `File exceeds ${maxFileSizeMb}MB limit`, icon: "toast-error" });
            return;
        }
        doUpload(file);
    };

    const handleRemove = async () => {
        const documentID = value?.documentID;
        setStatus("empty");
        setFileName("");
        setSizeBytes(null);
        setProgress(0);
        onChange(docType.code, null);
        if (documentID) {
            try {
                await deleteServiceDocument(documentID);
            } catch (err) {
                console.error("Failed to delete staged document:", err);
            }
        }
    };

    const handleRetry = () => (lastFileRef.current ? doUpload(lastFileRef.current) : openPicker());

    const hiddenInput = (
        <input
            ref={inputRef}
            type="file"
            accept={acceptAttr}
            style={{ display: "none" }}
            onChange={handleFileSelected}
            data-testid={`doc-input-${docType?.code}`}
        />
    );

    // ---- Empty: label + accepted exts + upload affordance (hover = dashed lavender) ----
    // When `error` is set (required doc missing on submit) the slot border turns
    // red and a helper message shows below.
    if (status === "empty") {
        return (
            <Box>
                <Box
                    onClick={openPicker}
                    data-testid={`doc-slot-${docType?.code}`}
                    sx={{
                        ...ROW,
                        border: error ? "1px solid #E5484D" : "1px solid #ECECEC",
                        backgroundColor: "#FFFFFF",
                        cursor: "pointer",
                        transition: "background-color .15s, border-color .15s",
                        "& .doc-icon": { backgroundColor: "#FFFFFF", "& svg": { color: "#667085" } },
                        "&:hover": {
                            border: "1px dashed #A5A9F8",
                            backgroundColor: "#F4F5FE",
                            "& .doc-icon": {
                                backgroundColor: "#EEF0FF",
                                boxShadow: "none",
                                "& svg": { color: "#6D72F6" },
                            },
                        },
                    }}
                >
                    {hiddenInput}
                    <IconTile IconComp={EmptyIcon} />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1A1A1A" }}>
                            {docType?.label}
                            {requiredMark}
                        </Typography>
                        {extLabel && (
                            <Typography sx={{ fontSize: "12px", color: "#8D8D8D" }}>{extLabel}</Typography>
                        )}
                    </Box>
                    <IconButton size="small" sx={{ color: "#8D8D8D" }}>
                        <FileUploadOutlinedIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </Box>
                {error && (
                    <Typography sx={{ fontSize: "12px", color: "#E5484D", mt: "6px", ml: "2px" }}>
                        This document is required
                    </Typography>
                )}
            </Box>
        );
    }

    // ---- Uploading: name + determinate progress ----
    if (status === "uploading") {
        return (
            <Box sx={{ ...ROW, border: "1px solid #ECECEC", backgroundColor: "#FFFFFF" }}>
                {hiddenInput}
                <IconTile IconComp={FileIcon} />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: "14px", color: "#1A1A1A", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {fileName}
                    </Typography>
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                            mt: "8px",
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: "#ECECEC",
                            "& .MuiLinearProgress-bar": { backgroundColor: "#6D72F6", borderRadius: 3 },
                        }}
                    />
                </Box>
            </Box>
        );
    }

    // ---- Failed: dashed red border + pink bg + Retry ----
    if (status === "failed") {
        return (
            <Box sx={{ ...ROW, border: "1px dashed #F04438", backgroundColor: "#FEF3F2" }}>
                {hiddenInput}
                <IconTile bg="#FFFFFF" color="#667085" IconComp={FileIcon} />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1A1A1A", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {fileName}
                        {requiredMark}
                    </Typography>
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "12px", color: "#8D8D8D" }}>
                        <Box component="span" sx={{ color: "#B42318" }}>
                            {errorMsg || "Upload Failed."}
                        </Box>
                        <Box
                            component="span"
                            onClick={handleRetry}
                            data-testid={`doc-retry-${docType?.code}`}
                            sx={{ display: "inline-flex", alignItems: "center", color: "#6D72F6", fontWeight: 500, cursor: "pointer", ml: "2px" }}
                        >
                            Retry <ReplayOutlinedIcon sx={{ fontSize: 14, ml: "2px" }} />
                        </Box>
                    </Box>
                </Box>
                <IconButton size="small" onClick={handleRemove} data-testid={`doc-remove-${docType?.code}`} sx={{ color: "#E5484D" }}>
                    <DeleteOutlineOutlinedIcon sx={{ fontSize: 20 }} />
                </IconButton>
            </Box>
        );
    }

    // ---- Uploaded: file icon + name + "docType  size" + remove ----
    return (
        <Box sx={{ ...ROW, border: "1px solid #ECECEC", backgroundColor: "#FFFFFF" }}>
            {hiddenInput}
            <IconTile IconComp={FileIcon} />
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1A1A1A", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {fileName}
                    {requiredMark}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "#8D8D8D" }}>
                    {docType?.label}
                    {sizeBytes != null && (
                        <Box component="span" sx={{ color: "#344054", fontWeight: 600, ml: "8px" }}>
                            {formatFileSize(sizeBytes)}
                        </Box>
                    )}
                </Typography>
            </Box>
            <IconButton size="small" onClick={handleRemove} data-testid={`doc-remove-${docType?.code}`} sx={{ color: "#E5484D" }}>
                <DeleteOutlineOutlinedIcon sx={{ fontSize: 20 }} />
            </IconButton>
        </Box>
    );
}
